import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, MenuItem, Select, FormControl, InputLabel, Checkbox, FormControlLabel
} from '@mui/material';
import { makeRequest } from '../../api';
import { Options } from "../../interfaces/Options";
import { Match } from "../../interfaces/Match";
import { HalfTimeRow } from "./HalfTimeRow";

interface MatchWithAvg {
    match: Match;
    avgTimer: string;
}

const timeToSeconds = (time: string) => {
    if (!time) return Infinity;
    const [minutes, seconds] = time.split(":").map(Number);
    return (minutes || 0) * 60 + (seconds || 0);
};

// Bandas de prioridad (segundos). htTimer = "Fin PT", avgTimer = FT estimado.
const HT_TIER1 = 45 * 60 + 10;   // 45:10
const FT_TIER12 = 90 * 60 + 30;  // 90:30
const FT_TIER3 = 91 * 60;        // 91:00
const FT_TIER4 = 92 * 60;        // 92:00

type Tier = 1 | 2 | 3 | 4 | 5;
const TIERS: { id: Tier; label: string; bg: string }[] = [
    { id: 1, label: '🔥 Tier 1 — HT < 45:10  y  FT < 90:30   (premium)', bg: '#b9f6ca' },
    { id: 2, label: '🟢 Tier 2 — FT < 90:30',                              bg: '#dcedc8' },
    { id: 3, label: '🟡 Tier 3 — FT < 91:00',                              bg: '#fff9c4' },
    { id: 4, label: '🟠 Tier 4 — FT < 92:00',                              bg: '#ffe0b2' },
    { id: 5, label: 'Otros',                                                bg: 'transparent' },
];

function tierOf(row: MatchWithAvg): Tier {
    const ftSec = row.avgTimer && row.avgTimer !== 'N/A' ? timeToSeconds(row.avgTimer) : Infinity;
    const htSec = row.match.htTimer ? timeToSeconds(row.match.htTimer) : Infinity;
    if (htSec < HT_TIER1 && ftSec < FT_TIER12) return 1;
    if (ftSec < FT_TIER12) return 2;
    if (ftSec < FT_TIER3) return 3;
    if (ftSec < FT_TIER4) return 4;
    return 5;
}

const HalfTimeTable = () => {
    const [data, setData] = useState<MatchWithAvg[]>([]);
    const [sortColumn, setSortColumn] = useState<string>('sHTimer');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [leagueFilter, setLeagueFilter] = useState<string>('all');
    const [htUnder46, setHtUnder46] = useState<boolean>(false);
    const [updatedAt, setUpdatedAt] = useState<string>('');

    const fetchData = useCallback(async () => {
        try {
            const options: Options = { method: 'get' };
            const response: any = await makeRequest('api/get_sh_potential_matches', options);
            if (response.status === 200) {
                setData(response.data as MatchWithAvg[]);
                setUpdatedAt(new Date().toLocaleTimeString());
            }
        } catch (e) {
            // makeRequest ya loggea
        }
    }, []);

    useEffect(() => {
        fetchData();
        const id = setInterval(fetchData, 30000); // auto-refresh
        return () => clearInterval(id);
    }, [fetchData]);

    const sortData = (column: string) => {
        const order = sortColumn === column && sortOrder === 'desc' ? 'asc' : 'desc';
        setSortOrder(order);
        setSortColumn(column);
    };

    const leagues = useMemo(() => {
        const uniqueLeagues = Array.from(new Set(data.map(d => d.match.league)));
        return uniqueLeagues.sort();
    }, [data]);

    // Filtros aplicados a todas las bandas
    const filteredData = useMemo(() => {
        return data.filter(({ match }) => {
            if (leagueFilter !== 'all' && match.league !== leagueFilter) return false;
            if (htUnder46 && timeToSeconds(match.htTimer) >= timeToSeconds('46:00')) return false;
            return true;
        });
    }, [data, leagueFilter, htUnder46]);

    // Ordenar dentro de cada banda según sortColumn/sortOrder
    const sortRows = (rows: MatchWithAvg[]) => {
        return [...rows].sort((a, b) => {
            let aValue: any = (a.match as any)[sortColumn];
            let bValue: any = (b.match as any)[sortColumn];
            if (sortColumn === 'sHTimer' || sortColumn === 'htTimer') {
                aValue = timeToSeconds(a.match[sortColumn as 'sHTimer' | 'htTimer']);
                bValue = timeToSeconds(b.match[sortColumn as 'sHTimer' | 'htTimer']);
            }
            if (aValue === bValue) return 0;
            return sortOrder === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
        });
    };

    // Agrupar por tier
    const tiers = useMemo(() => {
        const buckets: Record<Tier, MatchWithAvg[]> = { 1: [], 2: [], 3: [], 4: [], 5: [] };
        for (const row of filteredData) buckets[tierOf(row)].push(row);
        return buckets;
    }, [filteredData]);

    const tableHeaders = (
        <TableRow>
            <TableCell onClick={() => sortData('matchID')} style={{ cursor: 'pointer' }}>ID</TableCell>
            <TableCell onClick={() => sortData('league')} style={{ cursor: 'pointer' }}>Liga</TableCell>
            <TableCell>Partido</TableCell>
            <TableCell onClick={() => sortData('htTimer')} style={{ cursor: 'pointer' }}>Fin PT</TableCell>
            <TableCell onClick={() => sortData('sHTimer')} style={{ cursor: 'pointer' }}>Timer</TableCell>
            <TableCell onClick={() => sortData('totGoals')} style={{ cursor: 'pointer' }}>Tot Goles</TableCell>
            <TableCell onClick={() => sortData('difGoals')} style={{ cursor: 'pointer' }}>Diff Goles</TableCell>
            <TableCell>Under</TableCell>
            <TableCell>Avg Timer</TableCell>
            <TableCell>Link</TableCell>
        </TableRow>
    );

    return (
        <div>
            <h1>Partidos por banda de prioridad (FT estimado)</h1>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', margin: '10px 0', flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" onClick={fetchData}>Refresh</Button>
                <FormControl style={{ minWidth: 200 }}>
                    <InputLabel>Liga</InputLabel>
                    <Select value={leagueFilter} label="Liga" onChange={(e) => setLeagueFilter(e.target.value)}>
                        <MenuItem value="all">Todas</MenuItem>
                        {leagues.map(league => (
                            <MenuItem key={league} value={league}>{league}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Checkbox checked={htUnder46} onChange={(e) => setHtUnder46(e.target.checked)} />}
                    label="HT < 46:00"
                />
                <span style={{ color: '#888' }}>
                    {filteredData.length} partidos · T1:{tiers[1].length} · T2:{tiers[2].length} · T3:{tiers[3].length} · T4:{tiers[4].length} · act. {updatedAt}
                </span>
            </div>

            {TIERS.map(t => {
                const rows = sortRows(tiers[t.id]);
                if (rows.length === 0) return null;
                return (
                    <div key={t.id} style={{ marginBottom: 24 }}>
                        <h2 style={{ background: t.bg, padding: '6px 10px', borderRadius: 4, marginBottom: 8 }}>
                            {t.label} <span style={{ color: '#666', fontWeight: 'normal' }}>({rows.length})</span>
                        </h2>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>{tableHeaders}</TableHead>
                                <TableBody>
                                    {rows.map(({ match, avgTimer }) => (
                                        <HalfTimeRow key={match.matchID} match={match} avgTimer={avgTimer} />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                );
            })}
        </div>
    );
};

export { HalfTimeTable };
